import io from 'socket.io-client';
import _ from 'lodash';
import { FEED_TYPES, SOCKET_EVENTS, webSocket_URI } from '../constants/defaultValues';
import { store } from '../store/index';
import {
    PRODUCER_STATUSES_CHANGED,
    SET_SOCKET_CONNECTED,
    UPDATE_BET_SLIP_BET_LINES,
} from '../store/actions/types';
import { isEmpty, mergeMarkets } from './merger';
import { getMessageDetails } from '../store/actions/messages';
import { clearEventDetails, getEventDetails, setEventDetails } from '../store/actions/eventDetails';
import { saveFavoriteEvents } from '../store/actions/favorites';
import { setSocketConnectionCounter } from '../store/actions/socketAction';
import { setAppVersion } from '../store/actions/global';
import { getUserAccount, setUserAccount } from '../store/actions/user';
import { changeUnreadMessageCount } from '../store/actions/settings';
import { changeMessageModalProps } from '../store/actions/modals';
import { EVENT_FILTER_OPTIONS, generateEventFilters } from '../constants/event-filters';
import SocketManager from './SocketManager';
import EventRemoveModel from '../models/socket/event-remove/event-remove-model';
import BaseModel from '../models/BaseModel';
import EventAddModel from '../models/socket/event-add/event-add-model';
import EventScheduleUpdateModel from '../models/socket/event-schedule-update/event-schedule-update-model';
import EventLiveUpdateModel from '../models/socket/event-live-update/event-live-update-model';
import EventMarketRemoveModel from '../models/socket/event-market-remove/event-market-remove-model';
import FeedStatusUpdateModel from '../models/socket/feed-status-update/feed-status-update-model';
import EventMarketUpdateModel from '../models/socket/event-market-update/event-market-update-model';
import LiveModel from '../models/agent/live/live-model';
import {
    getEvents,
    getPopularEvents,
    getStaticData,
    setEvents,
    setPopularEvents,
} from '../store/actions/sportEvents';
import { getOutrights, setOutrights } from '../store/actions/outrights';
import OutrightAddModel from '../models/socket/outright-add/outright-add-model';
import { EVENT_TYPE_ENUM } from '../constants/enums';
import {
    fetchBetSlipEvents,
    getBetDetails,
    setPlaceBetErrors,
    setPlacedTicket,
    setRequestId,
} from '../store/actions/betSlip';
import { generateQuery } from './helpers';
import { getSportTypes } from '../store/actions/sportTypes';
import { fetchProducerStatuses } from '../store/actions/feed';

const socket = io(webSocket_URI, {
    transports: ['websocket'],
    reconnectionDelayMax: 1000,
    forceNew: true,
    timeout: 5000,
    // query: connectionContext,
});
socket.close();

let lastUpdateTime;
let timer;

export const updateSocket = (event, data) => {
    socket.emit(event, data);
};

socket.on('connect_error', error => {
    console.log('Socket connect_error', error);
    const reduxStore = store.getState();

    let connectionCounter = reduxStore.socket.connectionCounter;

    if (connectionCounter === 0) {
        connectionCounter++;
    }

    dispatch(setSocketConnectionCounter(connectionCounter));
});

socket.on('error', error => {
    console.log('Socket error', error);
});

socket.on('connect', () => {
    console.log('socket-update: connect', Date.now());
    updateLastUpdateTime();

    store.dispatch({ type: SET_SOCKET_CONNECTED, payload: true });

    const reduxStore = store.getState();

    if (!reduxStore.auth.isAuth) return;

    const socketContext = reduxStore.socket.context;
    let connectionCounter = reduxStore.socket.connectionCounter;

    connectionCounter++;

    dispatch(setSocketConnectionCounter(connectionCounter));
    socket.emit(SOCKET_EVENTS.connected, socketContext);

    if (connectionCounter > 1 && socketContext.isLive !== null) {
        fetchAllLiveData();
    }
});

socket.on('config', payload => {
    const { pingInterval, pingTimeout } = payload;

    if (timer) return;

    timer = setInterval(() => {
        socket.timeout(pingTimeout).emit('ping', error => {
            if (error && Date.now() - lastUpdateTime > pingInterval + pingTimeout) {
                console.log('socket timeout', {
                    error: !!error,
                    milliSecondsSinceLastUpdate: Date.now() - lastUpdateTime,
                });
                socket.disconnect();
                socket.connect();
            }
        });
    }, pingInterval);
});

socket.on('disconnect', reason => {
    console.log('Socket disconnect', reason);
    store.dispatch({ type: SET_SOCKET_CONNECTED, payload: false });
    // console.log('disconnected');
    // const socketContext = store.getState().socket.context;
    // console.log(socketContext);
    // socketConnected.current = false;
    // reconnectInterval = setInterval(() => {
    //     socket.connect();
    // }, 1000);
});

socket.on('reconnect', () => {
    // console.log('reconnect');
    // const reduxStore = store.getState();
    // const socketContext = reduxStore.socket.context;
    // const isAuth = reduxStore.auth.isAuth;
    // if (!isAuth) return;
});

socket.onAny(event => {
    console.log('socket-update: on any', Date.now(), event);
    updateLastUpdateTime();
});

const { dispatch } = store;

socket.on('event-update', async payload => {
    try {
        const { dispatch } = store;
        const reduxStore = store.getState();
        const { betLines } = reduxStore.betSlip;
        const {
            sortType,
            eventType,
            events: currentEvents,
            popularEvents,
        } = reduxStore.sportEvents;
        const eventDetails = reduxStore.eventDetails.event;
        const favoriteEvents = reduxStore.favorites.events;

        const isOutrightSelected = eventType.value === EVENT_TYPE_ENUM.outright;

        const { t: type, o: outright } = payload;

        if (type === 'event-add') {
            if (outright && isOutrightSelected) {
                SocketManager.handleOutrightAdd(
                    BaseModel.construct(OutrightAddModel, payload, { useFieldName: true }),
                );
            } else if (!outright && !isOutrightSelected) {
                SocketManager.handleEventAdd(
                    BaseModel.construct(EventAddModel, payload, { useFieldName: true }),
                );
            }
        } else if (type === 'event-remove') {
            const event = BaseModel.construct(EventRemoveModel, payload, { useFieldName: true });

            if (outright && isOutrightSelected) {
                SocketManager.handleOutrightRemove(event);
            } else if (!outright && !isOutrightSelected) {
                SocketManager.handleEventRemove(event);
            }

            removePopularEvent({ events: popularEvents, payload: event });
        } else if (type === 'event-live-update') {
            payload = BaseModel.construct(EventLiveUpdateModel, payload, {
                useFieldName: true,
            });

            SocketManager.updateBetLinesStatus({
                betLines,
                eventId: payload.eventId,
                changedProps: { isLive: payload.live },
            });

            updateFavoriteEvents({ favoriteEvents, payload });

            if (payload.eventId === eventDetails?.id) {
                const newEventDetails = _.cloneDeep(eventDetails);
                const { event } = updateEvent(newEventDetails, payload);
                dispatch(setEventDetails(event));
            }

            if (sortType === EVENT_FILTER_OPTIONS.orders.byLeague.id) {
                updateEventMarketsByLeague({
                    events: currentEvents,
                    payload,
                });
            } else if (
                sortType === EVENT_FILTER_OPTIONS.orders.byTime.id ||
                sortType === EVENT_FILTER_OPTIONS.orders.byMbn.id
            ) {
                updateEventMarketsByTime({
                    events: currentEvents,
                    payload,
                });
            }

            updatePopularEventMarkets({ events: popularEvents, payload });
        } else if (type === 'event-schedule-update') {
            payload = BaseModel.construct(EventScheduleUpdateModel, payload, {
                useFieldName: true,
            });

            if (payload.eventId === eventDetails?.id) {
                const newEventDetails = _.cloneDeep(eventDetails);
                const { event } = updateEvent(newEventDetails, payload);
                dispatch(setEventDetails(event));
            }

            if (sortType === EVENT_FILTER_OPTIONS.orders.byLeague.id) {
                updateEventMarketsByLeague({
                    events: currentEvents,
                    payload,
                });
            } else if (
                sortType === EVENT_FILTER_OPTIONS.orders.byTime.id ||
                sortType === EVENT_FILTER_OPTIONS.orders.byMbn.id
            ) {
                updateEventMarketsByTime({
                    events: currentEvents,
                    payload,
                });
            }

            updatePopularEventMarkets({ events: popularEvents, payload });
        }
    } catch (error) {
        console.error(error);
    }
});

socket.on('market-update', async payload => {
    try {
        const reduxStore = store.getState();
        const betLines = reduxStore.betSlip.betLines;
        const events = reduxStore.sportEvents.events;
        const popularEvents = reduxStore.sportEvents.popularEvents;
        const sortType = reduxStore.sportEvents.sortType;
        const outrights = reduxStore.outrights.seasons;
        const eventDetails = reduxStore.eventDetails.event;
        const favoriteEvents = reduxStore.favorites.events;

        const { t: type } = payload;

        if (type === 'event-market-update') {
            payload = BaseModel.construct(EventMarketUpdateModel, payload, {
                useFieldName: true,
            });

            updateBetLines({ betLines, payload });
            updateFavoriteEvents({ favoriteEvents, payload });

            if (SocketManager.isOutrightEvent(payload)) {
                updateOutrightEvents({ seasons: outrights, payload });
            } else {
                if (payload.eventId === eventDetails?.id) {
                    const newEventDetails = _.cloneDeep(eventDetails);
                    const { event } = updateEvent(newEventDetails, payload);
                    dispatch(setEventDetails(event));
                }

                if (sortType === EVENT_FILTER_OPTIONS.orders.byLeague.id) {
                    updateEventMarketsByLeague({ events, payload });
                } else if (
                    sortType === EVENT_FILTER_OPTIONS.orders.byTime.id ||
                    sortType === EVENT_FILTER_OPTIONS.orders.byMbn.id
                ) {
                    updateEventMarketsByTime({ events, payload });
                }

                updatePopularEventMarkets({ events: popularEvents, payload });
            }
        } else if (type === 'event-market-remove') {
            // TODO outright event market remove
            payload = BaseModel.construct(EventMarketRemoveModel, payload, {
                useFieldName: true,
            });

            if (!isEmpty(payload.marketIds)) {
                const { marketIds } = payload;

                if (betLines.length > 0) {
                    const newBetlines = betLines.map(betLine => {
                        if (
                            betLine.eventId === payload.eventId &&
                            marketIds.includes(betLine.marketId)
                        ) {
                            return {
                                ...betLine,
                                closed: true,
                            };
                        }
                        return betLine;
                    });

                    dispatch({
                        type: UPDATE_BET_SLIP_BET_LINES,
                        payload: newBetlines,
                    });
                }

                if (payload.eventId === eventDetails?.id) {
                    const newEventDetails = _.cloneDeep(eventDetails);

                    const { marketIds, marketCount } = payload;

                    newEventDetails.markets = newEventDetails.markets.filter(
                        market => !marketIds.includes(market.id),
                    );

                    if (!isEmpty(marketCount)) {
                        newEventDetails.moreMarkets = marketCount;
                    }

                    dispatch(setEventDetails(newEventDetails));
                }

                if (sortType === EVENT_FILTER_OPTIONS.orders.byLeague.id) {
                    deleteEventMarketsByLeague({
                        events,
                        payload,
                    });
                } else if (
                    sortType === EVENT_FILTER_OPTIONS.orders.byTime.id ||
                    sortType === EVENT_FILTER_OPTIONS.orders.byMbn.id
                ) {
                    deleteEventMarketsByTime({
                        events,
                        payload,
                    });
                }

                deletePopularEventMarkets({ events: popularEvents, payload });
            }
        }
    } catch (error) {
        console.error(error);
    }
});

socket.on('messages', async payload => {
    try {
        const { type, cashierIds, messageId, priorityStatus } = payload;

        const reduxStore = store.getState();
        const account = reduxStore.settings.account.data;

        if (cashierIds.includes(account.id) && type === 'message-new') {
            const { messageCount } = account;

            dispatch(changeUnreadMessageCount(messageCount + 1));

            if (priorityStatus === 2) {
                dispatch(getMessageDetails(messageId));
                dispatch(
                    changeMessageModalProps({
                        isVisible: true,
                        isNewMessage: true,
                    }),
                );
            }
        }
    } catch (error) {
        console.error(error);
    }
});

socket.on('feed-status-update', async payload => {
    try {
        const { t: type } = payload;

        if (type === 'feed-channel-status-change') {
            payload = BaseModel.construct(FeedStatusUpdateModel, payload, {
                useFieldName: true,
            });

            const { feedType, status } = payload;

            let producerStatuses = {};

            if (feedType === FEED_TYPES.preMatch) {
                producerStatuses.isPreMatchDown = status === 'DOWN';
            } else if (feedType === FEED_TYPES.live) {
                producerStatuses.isLiveDown = status === 'DOWN';
            }

            dispatch({
                type: PRODUCER_STATUSES_CHANGED,
                payload: producerStatuses,
            });
        }
    } catch (error) {
        console.error(error);
    }
});

socket.on('version-update', async payload => {
    try {
        const { appName, version } = payload;

        if (appName === 'cashier') {
            dispatch(setAppVersion(version));
        }
    } catch (error) {
        console.error(error);
    }
});

socket.on('balance-update', async payload => {
    try {
        const { type, amount } = payload;

        const reduxStore = store.getState();
        const account = reduxStore.settings.account.data;

        console.log('socket-update: balance', payload);

        if (type === 'current-balance') {
            dispatch(setUserAccount({ ...account, currentLimit: amount }));
        }
    } catch (error) {
        console.error(error);
    }
});

socket.on('ticket-creation', async payload => {
    try {
        const reduxStore = store.getState();
        const { requestId, couponData } = reduxStore.betSlip;

        console.log('socket-update: ticket', { payload, requestId });

        if (requestId === null) return;

        if (payload.requestId === requestId.requestId) {
            if (payload.ticket !== null) {
                dispatch(setPlacedTicket(payload.ticket));

                await dispatch(getBetDetails(requestId.requestId));
            } else {
                dispatch(setPlaceBetErrors([payload.error]));
            }

            dispatch(setRequestId(null));
        }
    } catch (error) {
        console.error(error);
    }
});

const updateLastUpdateTime = () => {
    lastUpdateTime = Date.now();
};

const updateEventMarketsByLeague = ({ events, payload }) => {
    const foundSeason = events.find(season => season.id === payload.leagueId);

    if (foundSeason) {
        const foundEvent = foundSeason.events.find(event => event.id === payload.eventId);

        if (foundEvent) {
            const foundSeason = events.find(season => season.id === payload.leagueId);
            const foundEvent = foundSeason.events.find(event => event.id === payload.eventId);

            const { hasUpdate, event: updatedEvent } = updateEvent(foundEvent, payload);

            const newEvents = events.map(season => {
                if (season.id === payload.leagueId) {
                    return {
                        ...season,
                        events: season.events.map(event => {
                            return event.id === payload.eventId ? updatedEvent : event;
                        }),
                    };
                }

                return season;
            });

            if (hasUpdate) {
                dispatch(
                    setEvents({
                        events: newEvents,
                    }),
                );
            }
        }
    }
};

const updateEventMarketsByTime = ({ events, payload }) => {
    const foundEvent = events.find(event => event.id === payload.eventId);

    if (foundEvent) {
        const { hasUpdate, event: updatedEvent } = updateEvent(foundEvent, payload);

        const newEvents = events.map(event => {
            return event.id === payload.eventId ? updatedEvent : event;
        });

        if (hasUpdate) {
            dispatch(
                setEvents({
                    events: newEvents,
                }),
            );
        }
    }
};

const updatePopularEventMarkets = ({ events, payload }) => {
    const foundEvent = events.find(event => event.id === payload.eventId);

    if (foundEvent) {
        const { hasUpdate, event: updatedEvent } = updateEvent(foundEvent, payload);

        const newEvents = events.map(event => {
            return event.id === payload.eventId ? updatedEvent : event;
        });

        if (hasUpdate) {
            dispatch(setPopularEvents(newEvents));
        }
    }
};

const deletePopularEventMarkets = ({ events, payload }) => {
    const foundEvent = events.find(event => event.id === payload.eventId);

    if (foundEvent) {
        const newEvents = events.map(event => {
            if (event.id === payload.eventId) {
                return {
                    ...event,
                    markets: event.markets.filter(market => !payload.marketIds.includes(market.id)),
                    moreMarkets: isEmpty(payload.marketCount)
                        ? event.moreMarkets
                        : payload.marketCount,
                };
            }

            return event;
        });

        dispatch(setPopularEvents(newEvents));
    }
};

const removePopularEvent = ({ events, payload }) => {
    if (events.find(event => event.id === payload.eventId)) {
        dispatch(setPopularEvents(events.filter(event => event.id !== payload.eventId)));
    }
};

const updateOutrightEvents = ({ seasons, payload }) => {
    const foundSeason = seasons.find(season => season.currentSeasonId === payload.eventId);

    if (foundSeason) {
        const { hasUpdate, season: updatedSeason } = updateOutrightSeason(foundSeason, payload);

        const newSeasons = seasons.map(season => {
            return season.currentSeasonId === payload.currentSeasonId ? updatedSeason : season;
        });

        if (hasUpdate) {
            dispatch(
                setOutrights({
                    seasons: newSeasons,
                }),
            );
        }
    }
};

const deleteEventMarketsByLeague = ({ events, payload }) => {
    const foundSeason = events.find(season => season.id === payload.leagueId);

    if (foundSeason) {
        const foundEvent = foundSeason.events.find(event => event.id === payload.eventId);

        if (foundEvent) {
            const newEvents = events.map(season => {
                if (season.id === payload.leagueId) {
                    return {
                        ...season,
                        events: season.events.map(event => {
                            if (event.id === payload.eventId) {
                                return {
                                    ...event,
                                    markets: event.markets.filter(
                                        market => !payload.marketIds.includes(market.id),
                                    ),
                                    moreMarkets: isEmpty(payload.marketCount)
                                        ? event.moreMarkets
                                        : payload.marketCount,
                                };
                            }

                            return event;
                        }),
                    };
                }

                return season;
            });

            dispatch(
                setEvents({
                    events: newEvents,
                }),
            );
        }
    }
};

const deleteEventMarketsByTime = ({ events, payload }) => {
    const foundEvent = events.find(event => event.id === payload.eventId);

    if (foundEvent) {
        const newEvents = events.map(event => {
            if (event.id === payload.eventId) {
                return {
                    ...event,
                    markets: event.markets.filter(market => !payload.marketIds.includes(market.id)),
                    moreMarkets: isEmpty(payload.marketCount)
                        ? event.moreMarkets
                        : payload.marketCount,
                };
            }

            return event;
        });

        dispatch(
            setEvents({
                events: newEvents,
            }),
        );
    }
};

const updateEvent = (event, payload) => {
    if (isEmpty(event.liveStatistics)) {
        event.liveStatistics = {};
    }

    if (!isEmpty(payload.liveStatistics)) {
        event.liveStatistics = BaseModel.merge(
            LiveModel,
            event.liveStatistics,
            payload.liveStatistics,
        );
    }

    if (!isEmpty(payload.newScheduleDate)) {
        event.startedAt = payload.newScheduleDate;
    }

    if (!isEmpty(payload.markets)) {
        event.markets = mergeMarkets(event.markets, payload.markets);
    }

    if (!isEmpty(payload.marketCount) && event.moreMarkets !== payload.marketCount) {
        event.moreMarkets = payload.marketCount;
    }

    if (!isEmpty(payload.eventMbn) && event.mbn !== payload.eventMbn) {
        event.mbn = payload.eventMbn;
    }

    return { hasUpdate: true, event };
};

const updateOutrightSeason = (season, payload) => {
    if (!isEmpty(payload.markets)) {
        season.markets = mergeMarkets(season.markets, payload.markets);
    }

    if (!isEmpty(payload.eventMbn) && season.mbn !== payload.eventMbn) {
        season.mbn = payload.eventMbn;
    }

    return { hasUpdate: true, season };
};

const updateBetLines = ({ betLines, payload }) => {
    if (betLines.length > 0) {
        let hasBetLinesChanged = false;

        const newBetlines = betLines.map(betLine => {
            const newBetLine = _.cloneDeep(betLine);

            if (newBetLine.eventId === payload.eventId) {
                hasBetLinesChanged = true;

                if (!isEmpty(payload.eventMbn)) {
                    newBetLine.mbn = payload.eventMbn;

                    if (Number(payload.eventMbn) < 1) {
                        newBetLine.closed = true;
                    }
                }

                if (!isEmpty(payload.markets)) {
                    const foundMarket = payload.markets.find(
                        market => market.id === newBetLine.marketId,
                    );

                    if (!isEmpty(foundMarket)) {
                        newBetLine.closed = foundMarket.closed;

                        if (!isEmpty(foundMarket.frozen)) {
                            newBetLine.frozen = foundMarket.frozen;
                        }

                        if (!isEmpty(foundMarket.mbn)) {
                            newBetLine.mbn = foundMarket.mbn;
                        }

                        if (!isEmpty(foundMarket.outcomes)) {
                            const foundOutcome = foundMarket.outcomes.find(
                                outcome => outcome.outcomeRefId === newBetLine.outcomeId,
                            );

                            if (foundOutcome) {
                                if (!foundMarket.closed) {
                                    newBetLine.closed = foundOutcome.closed;
                                }
                                if (!isEmpty(foundOutcome.odd)) {
                                    if (foundOutcome.odd > newBetLine.odd) {
                                        newBetLine.changeType = 'increase';
                                    } else if (foundOutcome.odd < newBetLine.odd) {
                                        newBetLine.changeType = 'decrease';
                                    } else {
                                        newBetLine.changeType = '';
                                    }
                                    newBetLine.odd = foundOutcome.odd;
                                    newBetLine.currentOdd = foundOutcome.odd;
                                }
                                if (!isEmpty(foundOutcome.status)) {
                                    newBetLine.outcomeStatus = foundOutcome.status;
                                }
                            }
                        }
                    }
                }

                if (newBetLine.removed) {
                    newBetLine.closed = true;
                }
            }

            return newBetLine;
        });

        if (hasBetLinesChanged) {
            dispatch({
                type: UPDATE_BET_SLIP_BET_LINES,
                payload: newBetlines,
            });
        }
    }
};

const updateFavoriteEvents = ({ favoriteEvents, payload }) => {
    if (favoriteEvents.find(event => event.id === payload.eventId)) {
        const newFavoriteEvents = favoriteEvents.map(favoriteEvent => {
            if (favoriteEvent.id === payload.eventId) {
                const newFavoriteEvent = _.cloneDeep(favoriteEvent);
                const { event } = updateEvent(newFavoriteEvent, payload);
                delete event.markets;

                return event;
            }

            return favoriteEvent;
        });

        dispatch(saveFavoriteEvents(newFavoriteEvents));
    }
};

const fetchAllLiveData = () => {
    const reduxStore = store.getState();

    const { isLive, isESports, eventType, sportRefId } = reduxStore.sportEvents;
    const { allMarketsEventId } = reduxStore.socket.context;

    const isOutrights = eventType.value === EVENT_TYPE_ENUM.outright;

    const filters = generateEventFilters({
        sportRefId,
        isLive,
        isESports,
        isOutrights,
        eventType: eventType.value,
    });
    const query = generateQuery(filters);

    dispatch(getUserAccount());
    dispatch(fetchBetSlipEvents());
    dispatch(fetchProducerStatuses());
    dispatch(getSportTypes({ isLive, eventType: eventType.value }));
    dispatch(
        getStaticData({
            sportTypeId: sportRefId,
            live: isLive,
            startDate: query.startDate,
            endDate: query.endDate,
            eventType: eventType.value,
            isESports,
            shouldUpdateEvents: false,
        }),
    );

    if (isOutrights) {
        dispatch(getOutrights({ pageOffset: 0 }));
    } else {
        dispatch(getEvents({ pageOffset: 0 }));

        if (!isLive) {
            dispatch(getPopularEvents());
        }
    }

    if (allMarketsEventId) {
        dispatch(clearEventDetails());
        dispatch(getEventDetails(allMarketsEventId));
    }
};

export default socket;
